import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, ArrowRight } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const ProjectAccess = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code || code.trim().length === 0) {
      toast.error('Please enter an access code');
      return;
    }
    
    try {
      setLoading(true);
      const project = await api.getProjectByShareCode(code.trim().toUpperCase());
      
      if (project) {
        navigate(`/view/${code.trim().toUpperCase()}`);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Invalid access code. Please check and try again.');
      } else {
        toast.error('Error accessing project');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#fafafa] dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8 border border-slate-200 bg-white shadow-sm">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-[#2d3748] dark:bg-white rounded flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-white dark:text-[#2d3748]" />
          </div>
          
          <h1 className="text-3xl font-bold text-[#1f2937] dark:text-white mb-2">
            Project Access
          </h1>
          <p className="text-[#6b7280] dark:text-slate-400">
            Enter your access code to view the project
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="access-code">Access Code</Label>
            <Input
              id="access-code"
              type="text"
              placeholder="e.g., ABC12345"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="text-center text-2xl font-mono tracking-wider"
              maxLength={8}
              data-testid="access-code-input"
            />
            <p className="text-xs text-slate-500 mt-2 text-center">
              8-character code provided by your project coordinator
            </p>
          </div>
          
          <Button
            type="submit"
            className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
            disabled={loading}
            data-testid="access-btn"
          >
            {loading ? 'Accessing...' : 'Access Project'}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </form>
        
        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
          <p className="text-xs text-center text-slate-500">
            Don't have an access code?{' '}
            <button
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Go to Dashboard
            </button>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ProjectAccess;